import { useReactFlow, NodeProps } from '@xyflow/react'
import { 
  MessageCircle, 
  Send, 
  Trash2, 
  Download, 
  Brain, 
  Copy,
  CheckCircle,
  AlertCircle,
  Sparkles
} from 'lucide-react'
import React, { memo, useRef, useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Badge } from '@/components/ui/badge'
import { ScrollArea } from '@/components/ui/scroll-area'
import useDataSettings from '@/hooks/useDataSettings'
import NodeBaseContainer from '../base/base-container/NodeBaseContainer'
import NodeConfigurationDialog from '../../dialogs/configuration-dialog/NodeConfigurationDialog'
import NodeDataSettingsDialog from '../../dialogs/data-settings-dialog/NodeDataSettingsDialog'

interface FacilitatorMessage {
  id: string
  role: 'user' | 'assistant' | 'system'
  content: string
  timestamp: string
  userId?: string
  userName?: string
  isExposed?: boolean
  aiPersonality?: string
  metadata?: {
    analysisGenerated?: boolean
    nodesCreated?: string[]
    dikwExtracted?: boolean
  }
}

interface NodeFacilitatorData {
  messages?: FacilitatorMessage[]
  conversationSummary?: string
  aiEngine?: 'xai' | 'openai'
  model?: string
  personality?: string
  autonomousLevel?: 'confirmation' | 'auto'
  contextScope?: 'global' | 'selected' | 'chat'
  maxTokens?: number
  temperature?: number
  selectedNodeIds?: string[]
  statusMessage?: string
  configuration?: any
  analysisEnabled?: boolean
  dikwExtractionEnabled?: boolean
  nodeCreationSuggestions?: boolean
  isLocked?: boolean
  isMinimized?: boolean
  assignedEmails?: string
  dataSettings?: any
  minimizeLevel?: 'normal' | 'medium' | 'minimum'
  label?: string
  pendingActions?: string[]
  facilitatorCommand?: {
    'sysagent-cmd': string
    processing?: boolean
    timestamp?: number
    message?: string
    aiEngine?: 'xai' | 'openai'
    model?: string
    personality?: string
    autonomousLevel?: 'confirmation' | 'auto'
    contextScope?: 'global' | 'selected' | 'chat'
    maxTokens?: number
    [key: string]: any
  }
  documentRequest?: {
    'sysagent-cmd': string
    processing?: boolean
    timestamp?: number
    message?: string
    aiEngine?: 'xai' | 'openai'
    model?: string
    personality?: string
    autonomousLevel?: 'confirmation' | 'auto'
    contextScope?: 'global' | 'selected' | 'chat'
    maxTokens?: number
    [key: string]: any
  }
  data?: {
    chat?: {
      lastResponse?: string
      tokensUsed?: number
      engine?: string
      model?: string
      exposedLines?: string[]
    }
  }
  actionsTotal?: number
}

const PERSONALITIES = [
  { value: 'default', label: 'Helpful Assistant', description: 'Balanced, professional AI assistant' },
  { value: 'facilitator', label: 'Meeting Facilitator', description: 'Guides discussions and extracts insights' },
  { value: 'analyst', label: 'Data Analyst', description: 'Focused on analysis and strategic insights' },
  { value: 'creative', label: 'Creative Collaborator', description: 'Encourages brainstorming and innovation' }
]

const NodeFacilitator: React.FC<NodeProps & { data: NodeFacilitatorData }> = (
  props
) => {  const { id, data, selected } = props
  const { updateNodeData } = useReactFlow()
  
  // ✅ CORRECT - Initialize with safe defaults, not collaborative state
  const [messages, setMessages] = useState<FacilitatorMessage[]>([])
  const [newMessage, setNewMessage] = useState('')
  const [aiEngine, setAiEngine] = useState<'xai' | 'openai'>('xai')
  const [model] = useState('grok-2-latest')
  const [personality, setPersonality] = useState('default')
  const [autonomousLevel] = useState<'confirmation' | 'auto'>('confirmation')
  const [contextScope] = useState<'global' | 'selected' | 'chat'>('global')
  const [maxTokens] = useState(0)
  const [temperature] = useState(0.7)
  const [statusMessage, setStatusMessage] = useState('')
  const [isConfigDialogVisible, setIsConfigDialogVisible] = useState(false)
  const [isDataSettingsDialogVisible, setIsDataSettingsDialogVisible] = useState(false)
  const [isLocked] = useState(false)
  const [isMinimized] = useState(false)

  const messageEndRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  // --- useDataSettings integration ---
  const prepareExposedDataFields = () => ({
    lastResponse: {
      value: data.data?.chat?.lastResponse || '',
      enabled: true,
      label: 'Last AI Response',
      description: 'The most recent response from the AI facilitator.',
      permissions: { readable: true, writable: false },
    },
    exposedLines: {
      value: (data.data?.chat?.exposedLines || []).join('\\n'),
      enabled: true,
      label: 'Exposed Chat Lines',
      description: 'Selected chat messages exposed for linking to other nodes.',
      permissions: { readable: true, writable: false },
    },
    conversationSummary: {
      value: data.conversationSummary || '',
      enabled: true,
      label: 'Conversation Summary',
      description: 'AI-generated summary of the conversation history.',
      permissions: { readable: true, writable: false },
    }
  })
  // Helper to sync node data
  const syncNodeData = (newData: Partial<NodeFacilitatorData>) => {
    updateNodeData(id, { ...data, ...newData })
  }

  // Configuration handler
  const handleConfigClick = () => {
    setIsConfigDialogVisible(true)
  }
  // useDataSettings hook
  const {
    dataSettings,
    setDataSettings,
    handleDataSettingsChange,
    handleRemoveInputLink,
    handleSyncInputLink,
  } = useDataSettings(
    id,
    data as any,
    syncNodeData,
    false,
    '',
    new Map(),
    '',
    setIsDataSettingsDialogVisible
  )
  // Keep exposedData in sync - only update local state from collaborative state
  useEffect(() => {
    const newExposed = prepareExposedDataFields()
    setDataSettings((prev) => ({
      ...prev,
      exposedData: newExposed,
    }))
    // ✅ REMOVED syncNodeData call - only update local state from collaborative state
  }, [data.data?.chat?.lastResponse, data.data?.chat?.exposedLines, data.conversationSummary])

  // Auto-scroll to bottom of messages
  useEffect(() => {
    messageEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])  
  
  // ✅ CORRECT - Sync from collaborative state to local state
  useEffect(() => {
    setMessages(data.messages || [])
    setStatusMessage(data.statusMessage || '')
  }, [data.messages, data.statusMessage])

  // ✅ CORRECT - Sync other collaborative state to local state  
  useEffect(() => {
    setAiEngine(data.aiEngine || 'xai')
    setPersonality(data.personality || 'default')
  }, [data.aiEngine, data.personality])

  // Use isProcessing flag from documentRequest
  const isProcessing = !!data.documentRequest?.processing  // Clear status message when processing finishes - only update local state

  useEffect(() => {
    if (!isProcessing && statusMessage === 'Processing...') {
      setStatusMessage('')
    }
  }, [isProcessing, statusMessage])

  const handleSendMessage = () => {
    if (!newMessage.trim() || isProcessing) return

    // Don't add user message to local state - let backend handle it
    const messageToSend = newMessage.trim()
    setNewMessage('')

    // ✅ CORRECT - Sync to collaborative state separately
    syncNodeData({
      aiEngine,
      model: mapModelName(model),
      personality,
      autonomousLevel,
      contextScope,
      maxTokens,
      temperature,
      statusMessage: 'Processing...',
      documentRequest: {
        'sysagent-cmd': 'Facilitator-Process-Started',
        processing: false,
        timestamp: Date.now(),
        message: messageToSend,
        aiEngine,
        model: mapModelName(model),
        personality,
        autonomousLevel,
        contextScope,
        maxTokens
      }
    })// Focus back to input
    setTimeout(() => inputRef.current?.focus(), 100)
  }

  const handleClearChat = () => {
    if (isProcessing) return
    
    // ✅ CORRECT - Only sync to collaborative state, local state will update via useEffect
    syncNodeData({
      messages: [],
      conversationSummary: '',
      statusMessage: '',
      data: {
        ...data.data,
        chat: {
          ...data.data?.chat,
          lastResponse: '',
          exposedLines: []
        }
      }
    })
  }
  const handleToggleExposed = (messageId: string) => {
    const updatedMessages = messages.map(msg => 
      msg.id === messageId 
        ? { ...msg, isExposed: !msg.isExposed }
        : msg
    )
    // ✅ CORRECT - Only sync to collaborative state, local state will update via useEffect
    syncNodeData({ messages: updatedMessages })
  }

  const handleCopyMessage = (content: string) => {
    navigator.clipboard.writeText(content)
  }

  const handleExportChat = () => {
    const exportData = {
      timestamp: new Date().toISOString(),
      conversation: messages,
      summary: data.conversationSummary,
      settings: {
        aiEngine,
        model,
        personality,
        autonomousLevel,
        contextScope
      }
    }
    
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `facilitator-chat-${id}-${Date.now()}.json`
    a.click()
    URL.revokeObjectURL(url)
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSendMessage()
    }
  }

  const renderMessage = (message: FacilitatorMessage) => {
    const isUser = message.role === 'user'
    const isSystem = message.role === 'system'
    
    return (
      <div
        key={message.id}
        className={`group flex ${isUser ? 'justify-end' : 'justify-start'} mb-4`}
      >
        <div
          className={`max-w-[80%] rounded-lg px-4 py-2 ${
            isUser
              ? 'bg-blue-500 text-white'
              : isSystem
              ? 'bg-gray-100 text-gray-800 border'
              : 'bg-gray-50 border'
          } ${message.isExposed ? 'ring-2 ring-green-400' : ''}`}
        >
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1">
              <div className="text-sm whitespace-pre-wrap">{message.content}</div>
              {message.metadata?.analysisGenerated && (
                <Badge variant="secondary" className="mt-2">
                  <Brain className="w-3 h-3 mr-1" />
                  Analysis Generated
                </Badge>
              )}
              {message.metadata?.nodesCreated && message.metadata.nodesCreated.length > 0 && (
                <Badge variant="secondary" className="mt-2 ml-2">
                  <Sparkles className="w-3 h-3 mr-1" />
                  {message.metadata.nodesCreated.length} Nodes Created
                </Badge>
              )}
            </div>
            <div className="flex opacity-0 group-hover:opacity-100 transition-opacity">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleCopyMessage(message.content)}
                className="h-6 w-6 p-0"
              >
                <Copy className="w-3 h-3" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleToggleExposed(message.id)}
                className="h-6 w-6 p-0"
              >
                {message.isExposed ? (
                  <CheckCircle className="w-3 h-3 text-green-500" />
                ) : (
                  <AlertCircle className="w-3 h-3" />
                )}
              </Button>
            </div>
          </div>
          <div className="text-xs opacity-60 mt-1">
            {isUser ? (message.userName || 'You') : 'AI'} • {new Date(message.timestamp).toLocaleTimeString()}
          </div>
        </div>
      </div>
    )
  }

  const personalityInfo = PERSONALITIES.find(p => p.value === personality)

  // Helper function to map frontend model values to backend model names
  const mapModelName = (frontendModel: string): string => {
    const modelMap: Record<string, string> = {
      'xai-grok-2-latest': 'grok-2-latest',
      'xai-grok-2-1212': 'grok-2-1212',
      'xai-grok-latest': 'grok-2-latest', // fallback for old values
      'xai-grok-beta': 'grok-2-latest', // fallback for invalid model
      'openai-4o': 'gpt-4',
      'openai-4.5': 'gpt-4-turbo',
      'openai-gpt-4': 'gpt-4',
      'openai-gpt-3.5': 'gpt-3.5-turbo'
    }
    
    return modelMap[frontendModel] || frontendModel || 'grok-2-latest'
  }

  return (
    <>      <NodeBaseContainer
        id={id}
        data={data}
        resizable={true}
        selected={selected}
        minWidth={300}
        minHeight={400}
        nodeTitle={data.label || 'AI Facilitator'}
        isLocked={isLocked}
        isMinimized={isMinimized}
        assignedEmails={
          data.assignedEmails ? data.assignedEmails.split(/\s+/).filter(Boolean) : []
        }
        onAssignUsersClick={() => {
          const emailsStr = window.prompt(
            'Enter emails separated by space',
            data.assignedEmails || ''
          )
          if (emailsStr !== null) {
            syncNodeData({ assignedEmails: emailsStr })
          }        }}
        customButtons={[
          {
            id: 'config',
            onClick: handleConfigClick,
            icon: React.createElement('svg', {
              width: 16,
              height: 16,
              viewBox: '0 0 24 24',
              fill: 'none',
              stroke: 'currentColor',
              strokeWidth: 2,
              strokeLinecap: 'round',
              strokeLinejoin: 'round'
            }, [
              React.createElement('path', { key: 1, d: 'M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z' }),
              React.createElement('circle', { key: 2, cx: 12, cy: 12, r: 3 })
            ]),
            tooltip: 'Configuration'
          }
        ]}        status={isProcessing ? 'loading' : undefined}
        className="w-80 h-auto min-h-[400px]"
      >
        <div className="flex flex-col h-full gap-4 p-4">
          {/* Settings Bar */}
          <div className="flex items-center justify-between gap-2 p-2 bg-gray-50 rounded">
            <div className="flex items-center gap-2">
              <Select value={aiEngine} onValueChange={(value) => setAiEngine(value as 'xai' | 'openai')}>
                <SelectTrigger className="w-20 h-8">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="xai">X.AI</SelectItem>
                  <SelectItem value="openai">OpenAI</SelectItem>
                </SelectContent>
              </Select>
              
              <Select value={personality} onValueChange={setPersonality}>
                <SelectTrigger className="w-32 h-8">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PERSONALITIES.map(p => (
                    <SelectItem key={p.value} value={p.value}>
                      {p.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleExportChat}
                className="h-8 w-8 p-0"
                disabled={messages.length === 0}
              >
                <Download className="w-4 h-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleClearChat}
                className="h-8 w-8 p-0"
                disabled={isProcessing || messages.length === 0}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Personality Description */}
          {personalityInfo && (
            <div className="text-xs text-gray-600 px-2">
              <Brain className="w-3 h-3 inline mr-1" />
              {personalityInfo.description}
            </div>
          )}          {/* Context Scope & Autonomy */}
          <div className="flex items-center justify-between text-xs text-gray-600 px-2">
            <div className="flex items-center gap-2">
              <span>Context: {contextScope}</span>
              <span>•</span>
              <span>Mode: {autonomousLevel}</span>
              {autonomousLevel === 'auto' && (
                <Badge variant="outline" className="text-xs bg-green-50 border-green-200">
                  Auto-creates nodes
                </Badge>
              )}
            </div>            {data.conversationSummary && (
              <Badge variant="outline" className="text-xs">
                Has Summary
              </Badge>
            )}
            {data.pendingActions && data.pendingActions.length > 0 && (
              <Badge variant="outline" className="text-xs bg-orange-50 border-orange-200">
                Awaiting Confirmation
              </Badge>
            )}
          </div>{/* Chat Messages */}
          <ScrollArea className="flex-1 min-h-[200px] border rounded-lg p-2">            {messages.length === 0 ? (
              <div className="flex items-center justify-center h-full text-gray-500 text-sm">
                <div className="text-center">
                  <MessageCircle className="w-8 h-8 mx-auto mb-2 opacity-30" />
                  <p>Start a conversation with your AI facilitator</p>
                  <p className="text-xs mt-1">Ask questions, brainstorm ideas, or get project insights</p>
                  {autonomousLevel === 'auto' && (
                    <p className="text-xs mt-2 text-blue-600">
                      💡 Try: "Create a document for our project plan" or "I need a strategy node"
                    </p>
                  )}
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                {messages.map(renderMessage)}
                <div ref={messageEndRef} />
              </div>
            )}
          </ScrollArea>

          {/* Message Input */}
          <div className="flex items-end gap-2">
            <div className="flex-1">
              <Input                ref={inputRef}
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder={
                  data.pendingActions && data.pendingActions.length > 0
                    ? "Reply 'yes' to confirm or 'no' to decline..."
                    : "Type your message..."
                }
                disabled={isProcessing || isLocked}
                className="resize-none"
              />
            </div>
            <Button
              onClick={handleSendMessage}
              disabled={!newMessage.trim() || isProcessing || isLocked}
              size="sm"
            >
              <Send className="w-4 h-4" />
            </Button>
          </div>

          {/* Status */}
          {statusMessage && (
            <div className="text-xs text-gray-600 px-2">
              {statusMessage}
            </div>
          )}
        </div>
      </NodeBaseContainer>      {/* Data Settings Dialog */}
      {isDataSettingsDialogVisible && (
        <NodeDataSettingsDialog
          visible={isDataSettingsDialogVisible}
          onHide={() => setIsDataSettingsDialogVisible(false)}
          settings={dataSettings}
          nodeId={id}
          onSettingsChange={handleDataSettingsChange}
          onRemoveInputLink={handleRemoveInputLink}
          onSyncInputLink={handleSyncInputLink}
          forceShowAvailableNodes={false}
          debug={false}
        />      )}

      {/* Configuration Dialog */}
      {isConfigDialogVisible && (
        <NodeConfigurationDialog
          visible={isConfigDialogVisible}          configuration={{
            isAutomated: false,
            modifierprompt: '',
            aiEngine: aiEngine,
            temperature: temperature,
            maxTokens: maxTokens,
            systemPrompt: data.configuration?.systemPrompt || '',
            analysisEnabled: data.analysisEnabled,
            dikwExtractionEnabled: data.dikwExtractionEnabled,
            nodeCreationSuggestions: data.nodeCreationSuggestions,
            autonomousLevel: autonomousLevel,
            contextScope: contextScope
          }}
          onSave={(newConfig: any) => {
            syncNodeData({
              configuration: {
                ...data.configuration,
                ...newConfig
              },
              analysisEnabled: newConfig.analysisEnabled,
              dikwExtractionEnabled: newConfig.dikwExtractionEnabled,
              nodeCreationSuggestions: newConfig.nodeCreationSuggestions
            })
            setIsConfigDialogVisible(false)
          }}
          onHide={() => setIsConfigDialogVisible(false)}
          additionalContent={(config, setConfig) => (
            <div className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">System Prompt</label>
                <textarea
                  className="w-full p-2 border rounded resize-y"
                  rows={3}
                  value={config.systemPrompt || ''}
                  onChange={(e) => setConfig({ ...config, systemPrompt: e.target.value })}
                  placeholder="Custom system prompt for the AI facilitator..."
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Max Tokens</label>                <input
                  type="number"
                  className="w-full p-2 border rounded"
                  value={config.maxTokens || 0}
                  onChange={(e) => setConfig({ ...config, maxTokens: parseInt(e.target.value) || 0 })}
                  min={0}
                  max={8000}
                  placeholder="Max tokens (0 = unlimited)"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Temperature</label>                <input
                  type="range"
                  className="w-full"
                  min={0}
                  max={2}
                  step={0.1}
                  value={config.temperature || 0.7}
                  onChange={(e) => setConfig({ ...config, temperature: parseFloat(e.target.value) })}
                  title="Temperature setting"
                />
                <div className="text-xs text-gray-500">Current: {config.temperature || 0.7}</div>
              </div>
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="analysisEnabled"
                    checked={config.analysisEnabled !== false}
                    onChange={(e) => setConfig({ ...config, analysisEnabled: e.target.checked })}
                  />
                  <label htmlFor="analysisEnabled" className="text-sm">Enable Analysis & Insights</label>
                </div>
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="dikwExtractionEnabled"
                    checked={config.dikwExtractionEnabled !== false}
                    onChange={(e) => setConfig({ ...config, dikwExtractionEnabled: e.target.checked })}
                  />
                  <label htmlFor="dikwExtractionEnabled" className="text-sm">Enable DIKW Extraction</label>
                </div>
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="nodeCreationSuggestions"
                    checked={config.nodeCreationSuggestions !== false}
                    onChange={(e) => setConfig({ ...config, nodeCreationSuggestions: e.target.checked })}
                  />
                  <label htmlFor="nodeCreationSuggestions" className="text-sm">Enable Node Creation Suggestions</label>
                </div>
              </div>
            </div>
          )}
        />
      )}
    </>
  )
}

export default memo(NodeFacilitator)
